// src/components/GuessHistory.js

import React from 'react';
import { compareCharacters } from '../utils/compareCharacters';
import { PatchNames } from '../data/patchNames';
import './GuessHistory.css';
import arrowUp from '../assets/arrow_up.png';
import arrowDown from '../assets/arrow_down.png';

function GuessHistory({ guesses, answer, onImageHover, onImageMouseLeave }) {
  // Base headers that are always included
  const baseHeaders = ['Image', 'Name'];

  // Function to get the list of headers based on the answer character's data
  const getHeaders = (answerCharacter) => {
    const headers = [...baseHeaders];

    // Define all possible attribute keys with their labels
    const attributeKeys = [
      { key: 'Home', label: 'Home', section: 'biographical_info' },
      { key: 'Age', label: 'Age', section: 'biographical_info' },
      {
        key: 'First Appearance',
        label: 'First Appearance',
        section: 'biographical_info',
      },
      { key: 'Affiliation', label: 'Affiliation', section: 'biographical_info' },
      { key: 'Race', label: 'Race', section: 'physical_description' },
      { key: 'Gender', label: 'Gender', section: 'physical_description' },
      {
        key: 'Hair color',
        label: 'Hair color',
        section: 'physical_description',
      },
      {
        key: 'Eye color',
        label: 'Eye color',
        section: 'physical_description',
      },
      { key: 'Type', label: 'Type', section: 'gameplay_details' },
      { key: 'Job class', label: 'Job class', section: 'gameplay_details' },
    ];

    // For each attribute, check if the answer character has data for it
    attributeKeys.forEach((attr) => {
      const sectionData = answerCharacter[attr.section] || {};
      if (
        sectionData[attr.key] !== undefined &&
        sectionData[attr.key] !== null
      ) {
        headers.push(attr.label);
      }
    });

    return headers;
  };

  // Generate headers based on the answer character's data
  const headers = getHeaders(answer);

  // Function to extract properties from a character
  const getCharacterData = (character) => {
    const data = {};

    data.Image = `/characters/${encodeURIComponent(
      character.name
    )}/image_1.png`;
    data.ImageFallback = `/characters/${encodeURIComponent(
      character.name
    )}/image_1.jpg`;
    data.Name = character.name || '-';

    const bio = character.biographical_info || {};
    const physical = character.physical_description || {};
    const gameplay = character.gameplay_details || {};

    // Map to store all attributes
    const allAttributes = {
      Home: formatProperty(bio.Home),
      Age:
        bio.Age !== undefined && bio.Age !== null ? bio.Age.toString() : '-',
      'First Appearance':
        bio['First Appearance'] !== undefined &&
        bio['First Appearance'] !== null
          ? formatFirstAppearance(bio['First Appearance'])
          : '-',
      Affiliation: formatProperty(bio.Affiliation),
      Race: formatProperty(physical.Race),
      Gender: formatProperty(physical.Gender),
      'Hair color': formatProperty(physical['Hair color']),
      'Eye color': formatProperty(physical['Eye color']),
      Type: formatProperty(gameplay.Type),
      'Job class': formatProperty(gameplay['Job class']),
    };

    // Only include attributes that are in the headers
    headers.forEach((header) => {
      if (header !== 'Image' && header !== 'Name') {
        data[header] = allAttributes[header] || '-';
      }
    });

    return data;
  };

  // Helper function to format properties
  function formatProperty(prop) {
    if (prop === undefined || prop === null) {
      return '-';
    }
    if (Array.isArray(prop)) {
      return prop.join(', ');
    }
    return prop.toString();
  }

  // Helper function to format First Appearance
  function formatFirstAppearance(patchNumber) {
    const patchKey = patchNumber.toString();
    const patchName = PatchNames[patchKey];
    if (patchName) {
      return `${patchNumber} - ${patchName}`;
    }
    return patchNumber.toString();
  }

  // Function to get comparison results for a character
  const getComparisonResults = (guessCharacter, answerCharacter) => {
    const comparisonResults = {};

    const feedback = compareCharacters(guessCharacter, answerCharacter);

    // Combine all sections into one object
    const details = {
      ...feedback.biographical_info,
      ...feedback.physical_description,
      ...feedback.gameplay_details,
    };

    // Map the results to match the headers
    for (const [key, data] of Object.entries(details)) {
      const headerKey = mapKeyToHeader(key);
      if (headers.includes(headerKey)) {
        comparisonResults[headerKey] = data.result;
      }
    }

    return comparisonResults;
  };

  // Function to map property keys to header keys
  const mapKeyToHeader = (key) => {
    // In this case, the keys match the headers, so we can return the key itself
    return key;
  };

  // Function to get CSS class based on result
  const getResultClass = (result) => {
    switch (result) {
      case 'Match':
        return 'match-cell';
      case 'Partial Match':
        return 'partial-match-cell';
      case 'No Match':
        return 'no-match-cell';
      case 'More':
      case 'Higher':
        return 'more-cell';
      case 'Less':
      case 'Lower':
        return 'less-cell';
      case 'Unknown':
        return 'unknown-cell'; // CSS class for unknown attributes
      default:
        return '';
    }
  };

  return (
    <div className="guess-history mt-8">
      <h2 className="text-2xl font-semibold text-palette-light-rose mb-4">
        Guess History
      </h2>
      <div className="overflow-x-auto">
        <table className="min-w-full table-auto bg-palette-light-rose shadow-md rounded-lg overflow-hidden border-4">
          <thead>
            <tr>
              {headers.map((header) => (
                <th
                  key={header}
                  className="px-4 py-2 bg-palette-lighter-blue text-white text-center text-sm uppercase tracking-wider border border-palette-light-rose"
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {guesses.map((guess, index) => {
              const characterData = getCharacterData(guess.character);
              const comparisonResults = getComparisonResults(
                guess.character,
                answer
              );

              return (
                <tr
                  key={index}
                  className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}
                >
                  {headers.map((header) => {
                    if (header === 'Image') {
                      return (
                        <td
                          key={header}
                          className="px-2 py-2 border text-center border-palette-light-rose bg-palette-dark-blue"
                        >
                          <img
                            src={characterData.Image}
                            alt={characterData.Name}
                            className="w-12 h-auto rounded-full cursor-pointer place-self-center "
                            data-fallback={characterData.ImageFallback}
                            onError={(e) => {
                              if (e.target.src.endsWith('image_1.png')) {
                                e.target.src = characterData.ImageFallback;
                              } else {
                                e.target.onerror = null;
                                e.target.src = '/default-character.png';
                              }
                            }}
                            onMouseEnter={() =>
                              onImageHover(characterData.Image)
                            }
                            onMouseLeave={onImageMouseLeave}
                          />
                        </td>
                      );
                    } else {
                      const result = comparisonResults[header];
                      const cellClass = getResultClass(result);

                      // Special handling for Age and First Appearance
                      if (
                        (header === 'Age' ||
                          header === 'First Appearance') &&
                        (result === 'More' || result === 'Less')
                      ) {
                        const arrowImage =
                          result === 'More' ? arrowDown : arrowUp;
                        return (
                          <td
                            key={header}
                            className={`px-4 py-2 text-center ${cellClass} border border-palette-light-rose bg-palette-dark-blue relative`}
                          >
                            <img
                              src={arrowImage}
                              alt={result}
                              className="absolute inset-0 w-full h-full object-contain opacity-30"
                              style={{ zIndex: 0 }}
                            />
                            <div className="relative z-10">
                              {characterData[header]}
                            </div>
                          </td>
                        );
                      } else {
                        return (
                          <td
                            key={header}
                            className={`px-4 py-2 ${cellClass} border text-center text-white border-palette-light-rose bg-palette-dark-blue`}
                          >
                            {characterData[header] || '-'}
                          </td>
                        );
                      }
                    }
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default GuessHistory;
